import { useState, useEffect, useCallback, createContext, useContext } from 'react';
import { useAuth } from './useAuth';
import { useThemeMode } from '../theme/ThemeProvider';
import userPreferencesService from '../services/userPreferencesService';

// Criar o Contexto
const UserPreferencesContext = createContext();

/**
 * Provider para gerenciar preferências do usuário globalmente
 */
export const UserPreferencesProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const { changeTheme } = useThemeMode();
  const [preferences, setPreferences] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Carregar preferências ao montar ou quando usuário mudar
  useEffect(() => {
    if (isAuthenticated && user?.id) {
      loadPreferences();
    } else {
      setIsLoading(false);
      setPreferences(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, user?.id]);


  // Carregar preferências
  const loadPreferences = useCallback(async () => {
    if (!user?.id) return;

    try {
      setIsLoading(true);
      setError(null);
      const prefs = await userPreferencesService.getPreferences();
      const defaultPrefs = {
        theme_mode: 'default',
        font_size: 'medium',
        menu_collapsed: false,
        menu_position: 'right',
        user_photo: null
      };
      const finalPrefs = prefs || defaultPrefs;
      setPreferences(finalPrefs);

      // Aplicar tema imediatamente ao carregar preferências
      if (finalPrefs.theme_mode && changeTheme) {
        changeTheme(finalPrefs.theme_mode);
      }
    } catch (err) {
      setError(err.message);
      // Usar valores padrão em caso de erro
      const defaultPrefs = {
        theme_mode: 'default',
        font_size: 'medium',
        menu_collapsed: false,
        menu_position: 'right', // Fallback seguro
        user_photo: null
      };
      setPreferences(defaultPrefs);
      if (changeTheme) {
        changeTheme('default');
      }
    } finally {
      setIsLoading(false);
    }
  }, [user?.id, changeTheme]);

  // Atualizar preferências
  const updatePreferences = useCallback(async (newPreferences) => {
    if (!user?.id) return false;

    try {
      setError(null);
      const success = await userPreferencesService.savePreferences(newPreferences);
      if (success) {
        setPreferences(prev => ({ ...prev, ...newPreferences }));
        return true;
      }
      return false;
    } catch (err) {
      setError(err.message);
      return false;
    }
  }, [user?.id]);

  // Atualizar tema
  const updateTheme = useCallback(async (themeMode) => {
    // Aplicar tema imediatamente
    if (changeTheme) {
      changeTheme(themeMode);
    }
    // Salvar nas preferências
    return await updatePreferences({ theme_mode: themeMode });
  }, [updatePreferences, changeTheme]);

  // Atualizar tamanho da fonte
  const updateFontSize = useCallback(async (fontSize) => {
    return await updatePreferences({ font_size: fontSize });
  }, [updatePreferences]);

  // Atualizar estado do menu
  const updateMenuCollapsed = useCallback(async (collapsed) => {
    return await updatePreferences({ menu_collapsed: collapsed });
  }, [updatePreferences]);

  // Salvar foto do usuário
  const saveUserPhoto = useCallback(async (photoBase64, metadata = {}) => {
    if (!user?.id) return false;

    try {
      setError(null);
      const success = await userPreferencesService.saveUserPhoto(photoBase64, metadata);
      if (success) {
        await loadPreferences(); // Recarregar para obter foto atualizada
        return true;
      }
      return false;
    } catch (err) {
      setError(err.message);
      return false;
    }
  }, [user?.id, loadPreferences]);

  // Remover foto do usuário
  const removeUserPhoto = useCallback(async () => {
    if (!user?.id) return false;

    try {
      setError(null);
      const success = await userPreferencesService.removeUserPhoto();
      if (success) {
        await loadPreferences(); // Recarregar para remover foto
        return true;
      }
      return false;
    } catch (err) {
      setError(err.message);
      return false;
    }
  }, [user?.id, loadPreferences]);

  const value = {
    preferences,
    isLoading,
    error,
    loadPreferences,
    updatePreferences,
    updateTheme,
    updateFontSize,
    updateMenuCollapsed,
    updateMenuPosition: async (position) => await updatePreferences({ menu_position: position }),
    saveUserPhoto,
    removeUserPhoto,
    // Valores diretos para facilitar uso
    themeMode: preferences?.theme_mode || 'default',
    fontSize: preferences?.font_size || 'medium',
    menuCollapsed: preferences?.menu_collapsed || false,
    menuPosition: preferences?.menu_position || 'right',
    userPhoto: preferences?.user_photo || null
  };

  return (
    <UserPreferencesContext.Provider value={value}>
      {children}
    </UserPreferencesContext.Provider>
  );
};

// Hook para consumir o contexto
export const useUserPreferences = () => {
  const context = useContext(UserPreferencesContext);
  if (!context) {
    throw new Error('useUserPreferences deve ser usado dentro de um UserPreferencesProvider');
  }
  return context;
};

export default useUserPreferences;
